// Command palette — the pure half (no React, no DOM).
//
// The palette answers one question: "take me to the page I mean,
// whichever of the nine apps it lives in". Wiz's Ctrl+K searches
// resources inside one product; X-One is a suite, so the gap a user
// feels is not "find a bucket" but "where is SBOMs again — Surface or
// Posture?". This module turns the SAME filtered sidebar data the
// shell renders into a flat, ranked list.
//
// Two rules keep it honest:
//
//  1. It never invents a destination. Entries come only from the
//     sources the caller passes, and the caller (AppLayout) passes the
//     sections it has already filtered by entitlement and feature flag.
//     A page the sidebar hides cannot appear here.
//  2. Ranking is deterministic and explainable: every query token must
//     match the label or its context, and label matches outrank context
//     matches, with initials as a middle tier ("sso" → Single sign-on).
//     No fuzzy-matching surprises where "sso" finds "Assets".

export interface PaletteSourceItem {
  to: string;
  label: string;
  /** A row that links to a page owned elsewhere (Policies, Configuration).
   *  It never takes the page's entry from the page's own row. */
  shortcut?: boolean;
  children?: PaletteSourceItem[];
}

export interface PaletteSource {
  /** The product or area the items belong to: "Aware", "Surface",
   *  "Organization". Shown as the result's context line. */
  group: string;
  /** Optional sub-heading within the group (a sidebar section title). */
  section?: string | null;
  /** The app these items belong to; absent for org-wide sections. */
  appKey?: string;
  items: PaletteSourceItem[];
}

export interface PaletteEntry {
  /** Stable id for ARIA (`aria-activedescendant`) and React keys. */
  id: string;
  /** Route path, hash fragment included when the item has one. */
  to: string;
  /** Display label, with decorative trailing arrows removed. */
  label: string;
  group: string;
  section: string | null;
  /** The parent row's label for nested items ("Phishing" for
   *  "Campaigns"), so two "Settings" rows read differently. */
  parent: string | null;
  /** Set for app pages; lets Focus lift them. */
  appKey: string | null;
}

/** Focus reorders and never hides. The bonus is below the smallest gap
 *  between match tiers, so it settles ties but never beats a better
 *  match: a page you named outranks a page in your focus. */
const FOCUS_BONUS = 8;

/** Lower-case, strip accents, turn punctuation into spaces, collapse. */
export function normalize(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Labels like "Orchestration ↗" carry a decorative arrow meaning
 *  "opens another app". The palette shows the app as context instead. */
function cleanLabel(label: string): string {
  return label.replace(/\s*[↗→]\s*$/u, '').trim();
}

/**
 * Flatten sources into entries, depth-first, keeping the first
 * occurrence of each route. Sources are passed in priority order, so a
 * route that appears both in an app's own sidebar and as a tenant-wide
 * shortcut keeps its more specific app context. Shortcut rows go last,
 * so "KPIs" stays "KPIs" however early a menu links to the same page.
 */
export function buildPaletteEntries(sources: PaletteSource[]): PaletteEntry[] {
  const seen = new Set<string>();
  const out: PaletteEntry[] = [];
  const walk = (items: PaletteSourceItem[], src: PaletteSource, parent: string | null, shortcuts: boolean) => {
    for (const it of items) {
      const label = cleanLabel(it.label);
      if (!!it.shortcut === shortcuts && !seen.has(it.to) && label.length > 0 && it.to.startsWith('/')) {
        seen.add(it.to);
        out.push({
          id: `palette-${out.length}`,
          to: it.to,
          label,
          group: src.group,
          section: src.section ?? null,
          parent,
          appKey: src.appKey ?? null,
        });
      }
      if (it.children && it.children.length > 0) walk(it.children, src, label, shortcuts);
    }
  };
  for (const shortcuts of [false, true]) {
    for (const src of sources) walk(src.items, src, null, shortcuts);
  }
  return out;
}

/** The context line under a result: "Aware › Phishing". */
export function contextLine(e: PaletteEntry): string {
  const parts = [e.group];
  if (e.section && normalize(e.section) !== normalize(e.group)) parts.push(e.section);
  if (e.parent) parts.push(e.parent);
  return parts.join(' › ');
}

function scoreToken(token: string, label: string, labelWords: string[], acronym: string, ctx: string, ctxWords: string[]): number {
  if (label.startsWith(token)) return 100;
  if (labelWords.some((w) => w.startsWith(token))) return 60;
  // "sso" → Single sign-on, "mfa" stays a word match. Two letters
  // minimum, so a lone "s" is a prefix query, not an acronym.
  if (token.length >= 2 && labelWords.length >= 2 && acronym.startsWith(token)) return 50;
  // Substring tiers need two letters: a lone "u" matching the u in
  // "sec-u-rity" is noise, not intent. One letter matches word starts only.
  if (token.length >= 2 && label.includes(token)) return 30;
  if (ctxWords.some((w) => w.startsWith(token))) return 15;
  if (token.length >= 2 && ctx.includes(token)) return 5;
  return 0;
}

/**
 * Rank entries for a query.
 *
 * Empty query → recently visited pages first (most recent first), then
 * pages in the person's focus, then everything else in sidebar order.
 * With a query, every token must match somewhere; label matches outrank
 * context matches; an exact label match wins outright; recency and focus
 * break ties.
 */
export function rankPaletteEntries(
  entries: PaletteEntry[],
  query: string,
  recents: string[],
  limit = 30,
  focusAppKeys: ReadonlySet<string> | null = null,
): PaletteEntry[] {
  const recentRank = new Map(recents.map((to, i) => [to, i] as const));
  const inFocus = (e: PaletteEntry) => e.appKey !== null && !!focusAppKeys?.has(e.appKey);
  const q = normalize(query);
  if (q === '') {
    const recent = recents
      .map((to) => entries.find((e) => e.to === to))
      .filter((e): e is PaletteEntry => !!e);
    const rest = entries.filter((e) => !recentRank.has(e.to));
    return [...recent, ...rest.filter(inFocus), ...rest.filter((e) => !inFocus(e))].slice(0, limit);
  }
  const tokens = q.split(' ');
  const scored: Array<{ e: PaletteEntry; score: number; order: number }> = [];
  entries.forEach((e, order) => {
    const label = normalize(e.label);
    const labelWords = label.split(' ');
    const acronym = labelWords.map((w) => w[0] ?? '').join('');
    const ctx = normalize(contextLine(e));
    const ctxWords = ctx.split(' ');
    let score = 0;
    for (const t of tokens) {
      const s = scoreToken(t, label, labelWords, acronym, ctx, ctxWords);
      if (s === 0) return; // every token must match
      score += s;
    }
    if (label === q) score += 200;
    const r = recentRank.get(e.to);
    if (r !== undefined) score += Math.max(1, 10 - r);
    if (inFocus(e)) score += FOCUS_BONUS;
    scored.push({ e, score, order });
  });
  scored.sort((a, b) => b.score - a.score || a.e.label.length - b.e.label.length || a.order - b.order);
  return scored.slice(0, limit).map((s) => s.e);
}

/** Move `to` to the front of the recents list, de-duplicated, capped. */
export function pushRecent(recents: string[], to: string, max = 6): string[] {
  return [to, ...recents.filter((r) => r !== to)].slice(0, max);
}

/**
 * Browser-tab title: "Campaigns · Aware · X-One Cyber Aware".
 * Parts that repeat the previous one are dropped, so the Aware
 * dashboard reads "Aware · X-One Cyber Aware", not "Aware · Aware · …".
 */
export function documentTitleFor(pageTitle: string | null | undefined, appName: string | null | undefined, site: string): string {
  const parts: string[] = [];
  for (const p of [pageTitle, appName, site]) {
    const v = (p ?? '').trim();
    if (v && (parts.length === 0 || normalize(parts[parts.length - 1]) !== normalize(v))) parts.push(v);
  }
  return parts.join(' · ');
}

/** True when a keyboard event's target is a field the user is typing in,
 *  so single-key shortcuts like "/" never steal a keystroke. */
export function isTypingTarget(el: { tagName?: string; isContentEditable?: boolean } | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName ?? '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
